import { format } from 'date-fns';

import { InlineDatePicker } from '@components/datepicker';

import { EditableCellPopover, useInlineSave } from './editable-cell-popover';
import { EditableCellEditorProps } from './types';
import { useEditableCellTrigger } from './use-editable-cell-trigger';

import { typeRenderMap } from '../renderers';

type EditableDateCellProps = EditableCellEditorProps<string | undefined>;

const DATE_PARTS_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateValue = (value: string | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const match = DATE_PARTS_PATTERN.exec(value);
  if (!match) {
    return undefined;
  }
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const EditableDateCell = ({ column, row, value }: EditableDateCellProps) => {
  const getTrigger = useEditableCellTrigger();
  const { error, save } = useInlineSave();

  const trigger = getTrigger(value, column, row, () => typeRenderMap.date(value, column, row));

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      isCommitOnClick
      validationError={error}
    >
      {(close) => (
        <InlineDatePicker
          defaultValue={parseDateValue(value)}
          onChange={(date) => {
            const nextValue = format(date, 'yyyy-MM-dd');

            // Picking the date the cell already holds is the click that closes
            // the picker, not a save.
            if (nextValue === value) {
              close();
              return;
            }

            save(async () => {
              await column.onEdit?.(row, nextValue);
            }, close);
          }}
        />
      )}
    </EditableCellPopover>
  );
};
